import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { EventDefinition, HttpClient } from '@wix/sdk-types';
import {
  ShippingOption,
  ShippingOptionCreatedEnvelope,
  ShippingOptionDeletedEnvelope,
  ShippingOptionNonNullableFields,
  ShippingOptionUpdatedEnvelope,
  ShippingOptionsQueryBuilder,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsResponse,
  UpdateExtendedFieldsResponseNonNullableFields,
  UpdateShippingOption,
  createShippingOption as universalCreateShippingOption,
  deleteShippingOption as universalDeleteShippingOption,
  getShippingOption as universalGetShippingOption,
  queryShippingOptions as universalQueryShippingOptions,
  updateExtendedFields as universalUpdateExtendedFields,
  updateShippingOption as universalUpdateShippingOption,
} from './ecom-v1-shipping-option-shipping-options.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/ecom' };

export function createShippingOption(
  httpClient: HttpClient
): CreateShippingOptionSignature {
  return (shippingOption: ShippingOption) =>
    universalCreateShippingOption(
      shippingOption,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateShippingOptionSignature {
  /**
   * Creates a ShippingOption.
   *
   * The request body must include shipping option and delivery region associated to.
   * @param - ShippingOption to be created.
   * @returns The created ShippingOption.
   */
  (shippingOption: ShippingOption): Promise<
    ShippingOption & ShippingOptionNonNullableFields
  >;
}

export function getShippingOption(
  httpClient: HttpClient
): GetShippingOptionSignature {
  return (shippingOptionId: string) =>
    universalGetShippingOption(
      shippingOptionId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetShippingOptionSignature {
  /**
   * Retrieves a ShippingOption.
   * @param - ID of the ShippingOption to retrieve.
   * @returns The requested ShippingOption.
   */
  (shippingOptionId: string): Promise<
    ShippingOption & ShippingOptionNonNullableFields
  >;
}

export function updateShippingOption(
  httpClient: HttpClient
): UpdateShippingOptionSignature {
  return (_id: string | null, shippingOption: UpdateShippingOption) =>
    universalUpdateShippingOption(
      _id,
      shippingOption,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateShippingOptionSignature {
  /**
   * Updates a ShippingOption.
   *
   *
   * Each time the ShippingOption is updated,
   * `revision` increments by 1.
   * The current `revision` must be passed when updating the ShippingOption.
   * This ensures you're working with the latest ShippingOption
   * and prevents unintended overwrites.
   * @param - ShippingOption ID.
   * @returns Updated ShippingOption.
   */
  (_id: string | null, shippingOption: UpdateShippingOption): Promise<
    ShippingOption & ShippingOptionNonNullableFields
  >;
}

export function deleteShippingOption(
  httpClient: HttpClient
): DeleteShippingOptionSignature {
  return (shippingOptionId: string) =>
    universalDeleteShippingOption(
      shippingOptionId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteShippingOptionSignature {
  /**
   * Deletes a ShippingOption.
   * Deleting a ShippingOption permanently removes them from the ShippingOption List.
   * @param - Id of the ShippingOption to delete.
   */
  (shippingOptionId: string): Promise<void>;
}

export function queryShippingOptions(
  httpClient: HttpClient
): QueryShippingOptionsSignature {
  return () =>
    universalQueryShippingOptions(
      // @ts-ignore
      { httpClient }
    );
}

interface QueryShippingOptionsSignature {
  /**
   * Retrieves a list of ShippingOptions, given the provided [paging, filtering, and sorting][1].
   *
   * Up to 1,000 ShippingOptions can be returned per request.
   *
   * To learn how to query ShippingOptions, see [API Query Language][2].
   *
   * [1]: https://dev.wix.com/api/rest/getting-started/sorting-and-paging
   * [2]: https://dev.wix.com/api/rest/getting-started/api-query-language
   */
  (): ShippingOptionsQueryBuilder;
}

export function updateExtendedFields(
  httpClient: HttpClient
): UpdateExtendedFieldsSignature {
  return (
    _id: string,
    namespace: string,
    options: UpdateExtendedFieldsOptions
  ) =>
    universalUpdateExtendedFields(
      _id,
      namespace,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateExtendedFieldsSignature {
  /**
   * Updates extended fields of a ShippingOption without incrementing revision
   * @param - ID of the entity to update.
   * @param - Identifier for the app whose extended fields are being updated.
   */
  (
    _id: string,
    namespace: string,
    options: UpdateExtendedFieldsOptions
  ): Promise<
    UpdateExtendedFieldsResponse & UpdateExtendedFieldsResponseNonNullableFields
  >;
}

export const onShippingOptionCreated = EventDefinition(
  'wix.ecom.v1.shipping_option_created',
  true,
  (event: ShippingOptionCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ShippingOptionCreatedEnvelope>();
export const onShippingOptionDeleted = EventDefinition(
  'wix.ecom.v1.shipping_option_deleted',
  true,
  (event: ShippingOptionDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ShippingOptionDeletedEnvelope>();
export const onShippingOptionUpdated = EventDefinition(
  'wix.ecom.v1.shipping_option_updated',
  true,
  (event: ShippingOptionUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
      ])
    )
)<ShippingOptionUpdatedEnvelope>();

export {
  ActionEvent,
  BaseEventMetadata,
  Condition,
  ConditionType,
  ConditionalRates,
  CreateShippingOptionRequest,
  CreateShippingOptionResponse,
  CreateShippingOptionResponseNonNullableFields,
  CursorPaging,
  CursorPagingMetadata,
  CursorQuery,
  CursorQueryPagingMethodOneOf,
  Cursors,
  DeleteShippingOptionRequest,
  DeleteShippingOptionResponse,
  DomainEvent,
  DomainEventBodyOneOf,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  ExtendedFields,
  GetShippingOptionRequest,
  GetShippingOptionResponse,
  GetShippingOptionResponseNonNullableFields,
  IdentificationData,
  IdentificationDataIdOneOf,
  LogicalOperator,
  MessageEnvelope,
  QueryShippingOptionsRequest,
  QueryShippingOptionsResponse,
  QueryShippingOptionsResponseNonNullableFields,
  RestoreInfo,
  ShippingOption,
  ShippingOptionCreatedEnvelope,
  ShippingOptionDeletedEnvelope,
  ShippingOptionNonNullableFields,
  ShippingOptionUpdatedEnvelope,
  ShippingOptionsQueryBuilder,
  ShippingOptionsQueryResult,
  SortOrder,
  Sorting,
  UpdateExtendedFieldsOptions,
  UpdateExtendedFieldsRequest,
  UpdateExtendedFieldsResponse,
  UpdateExtendedFieldsResponseNonNullableFields,
  UpdateShippingOption,
  UpdateShippingOptionRequest,
  UpdateShippingOptionResponse,
  UpdateShippingOptionResponseNonNullableFields,
  WebhookIdentityType,
} from './ecom-v1-shipping-option-shipping-options.universal.js';
